import { JourneyBags, JourneyExtras } from '@models/booking.models';
import { N0SsrPassengerUpdateRequest } from '@models/requests/ssr-passenger-update-request.model';
import { BundleService, OldSsrPrices, SaveOnlineLabels, SsrBundlesCopy } from "@models/cms/extras.model";
import { N0SsrByJourney } from "@models/responses/ssr-availability-response.model";
import { tryParseJson } from "@utils/json-utils";
import {CabinClassWithExtrasBundle, CabinClassWithoutExtrasBundle} from "@constants/cabin.constants";

export const convertBookingBagsToSsrUpdate = (
  journeyBags: JourneyBags,
): N0SsrPassengerUpdateRequest => {
  return {
    journeyKey: journeyBags.journeyKey,
    passengers: journeyBags.passengerBags.map((p) => ({
      passengerKey: p.passengerKey,
      ssrs: p.bags.map((b) => ({
        ssrCode: b.ssrCode,
        count: b.inBundleCount > b.paidCount ? b.inBundleCount : b.paidCount,
      })),
    })),
  };
};

export const convertBookingExtrasToSsrUpdate = (
  journeyExtras: JourneyExtras,
): N0SsrPassengerUpdateRequest => {
  return {
    journeyKey: journeyExtras.journeyKey,
    passengers: journeyExtras.passengerExtras.map((p) => ({
      passengerKey: p.passengerKey,
      ssrs: p.extras.map((b) => ({
        ssrCode: b.ssrCode,
        count: b.inBundleCount > b.paidCount ? b.inBundleCount : b.paidCount,
      })),
    })),
  };
};
export function tryParseOldSsrPrices(input?: string): OldSsrPrices | null {
  if (!input) {
    return null;
  }

  const parsed = tryParseJson(input);

  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }

  const result: OldSsrPrices = {};

  for (const ssrCode in parsed) {
    const airports = parsed[ssrCode];
    if (typeof airports !== 'object' || airports === null) {
      continue;
    }

    result[ssrCode] = {};

    for (const airportCode in airports) {
      const airportRates = airports[airportCode];
      if (typeof airportRates !== 'object' || airportRates === null) {
        continue;
      }

      const rates: { [currency: string]: number } = {};
      for (const currency in airportRates) {
        const price = airportRates[currency];
        if (typeof price === 'number' && price > 0) {
          rates[currency] = price;
        }
      }

      if (Object.keys(rates).length > 0) {
        result[ssrCode]![airportCode] = rates;
      }
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

export function addOldSsrPriceWithLabel({
  journeySsr,
  oldSsrPrices,
  saveOnlineLabels,
  currency
} : {
  journeySsr: N0SsrByJourney,
  oldSsrPrices: OldSsrPrices,
  saveOnlineLabels: SaveOnlineLabels,
  currency: string
}): N0SsrByJourney {
  return {
    ...journeySsr,
    extrasSsrs: journeySsr.extrasSsrs.map(ssr => {
      const originAirportPrices =
        oldSsrPrices[ssr.ssrCode]?.[journeySsr.journeyDetails.origin] ?? oldSsrPrices[ssr.ssrCode]?.['ALL'];

      const originAirportLabels =
        saveOnlineLabels[ssr.ssrCode]?.[journeySsr.journeyDetails.origin] ?? saveOnlineLabels[ssr.ssrCode]?.['ALL'];

      if (!originAirportPrices || !originAirportLabels) {
        return ssr;
      }

      const oldPrice = originAirportPrices[currency];
      const label = originAirportLabels[currency];

      if (!oldPrice || !label) {
        return ssr;
      }

      return {
        ...ssr,
        passengersAvailability: ssr.passengersAvailability.map(passengerAvailability => ({
          ...passengerAvailability,
          saveOnlineLabel: label,
          oldPrice,
        }))
      }
    }),
  };
}

export function tryParseSsrBundles(input?: string): SsrBundlesCopy | null {
  if (!input) {
    return null;
  }

  const parsed = tryParseJson(input);

  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }

  const result: SsrBundlesCopy = {};

  for (const airportCode in parsed) {
    const airportData = parsed[airportCode];
    if (typeof airportData !== 'object' || airportData === null) {
      continue;
    }

    const { services, header, copy, image, pillOne, pillTwo } = airportData;

    if (
      !Array.isArray(services) ||
      typeof header !== 'string' ||
      typeof copy !== 'string' ||
      typeof image !== 'string' ||
      typeof pillOne !== 'string' ||
      typeof pillTwo !== 'string'
    ) {
      continue;
    }

    const validServices: BundleService[] = services.filter((service): service is BundleService =>
      typeof service.code === 'string' && typeof service.description === 'string'
    );

    if (validServices.length === 0) {
      continue;
    }

    result[airportCode] = {
      services: validServices,
      header,
      copy,
      image,
      pillOne,
      pillTwo,
    };
  }

  return Object.keys(result).length > 0 ? result : null;
}

export function addSsrBundleWithStrikethroughPrices({
  journeySsr,
  ssrBundlesCopy,
}: {
  journeySsr: N0SsrByJourney;
  ssrBundlesCopy: SsrBundlesCopy;
}): N0SsrByJourney {
  const originAirport = journeySsr.journeyDetails.origin;
  const ssrBundle = ssrBundlesCopy[originAirport];

  if (!ssrBundle) {
    return journeySsr;
  }

  const ssrsInBundle = journeySsr.extrasSsrs
    .filter(ssr =>
      ssrBundle.services.some(service => service.code === ssr.ssrCode)
    );

  const prices = ssrsInBundle
    .map(ssr => ssr.passengersAvailability[0]?.price || 0)
    .sort((a, b) => b - a);

  const strikethroughPrice = prices.reduce((total, price) => total + price, 0);

  return {
    ...journeySsr,
    ssrBundle,
    bundleStrikethroughPrice: Math.ceil(strikethroughPrice),
  };
}

export function getExtrasBundleCode(currentBundleCode: string): string | undefined {
  if (currentBundleCode in CabinClassWithExtrasBundle) {
    return CabinClassWithExtrasBundle[currentBundleCode as keyof typeof CabinClassWithExtrasBundle];
  }
  if (currentBundleCode in CabinClassWithoutExtrasBundle) {
    return currentBundleCode;
  }
  return undefined;
}
